/*
 * Purpose: Compact Flow Template card — dashboard + inline spotlight
 * Last modified: 2026-07-01 | Agent
 * Completeness: 100
 */
import { Link } from 'react-router-dom';
import { Copy, Map } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { FlowTemplateRecord } from '@/lib/flowTemplates';
import { buildClonedFlowTemplate, flowTemplateDisplayName } from '@/lib/flowTemplates';
import { APP_ROUTES } from '@/lib/appRoutes';
import { useWorkflowStore } from '@/stores/workflowStore';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export function FlowTemplateQuickCard({
  template,
  className,
}: {
  template: FlowTemplateRecord;
  className?: string;
}) {
  const importWorkflow = useWorkflowStore((s) => s.importWorkflow);

  const handleClone = () => {
    importWorkflow(buildClonedFlowTemplate(template));
    toast.success('Flow cloned — open Map to customize', {
      action: {
        label: 'Open flows',
        onClick: () => {
          window.location.href = APP_ROUTES.flows;
        },
      },
    });
  };

  const displayName = flowTemplateDisplayName(template.name);

  return (
    <Card className={cn('p-4 flex flex-col gap-3 ui-lift-card h-full', className)}>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-wrap items-start gap-2">
          <h3 className="font-semibold text-sm text-foreground leading-snug">{displayName}</h3>
          <Badge variant="outline" className="text-[10px] shrink-0">
            {template.difficulty}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground line-clamp-2">{template.description}</p>
        <p className="text-[10px] text-muted-foreground">
          {template.nodes.length} blocks · {template.category}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" actionTier="secondary" className="h-8 text-xs flex-1 min-w-[7rem]" onClick={handleClone}>
          <Copy className="h-3.5 w-3.5 mr-1" />
          Clone
        </Button>
        <Button size="sm" actionTier="ghost" className="h-8 text-xs" asChild>
          <Link to={APP_ROUTES.flowTemplates}>
            <Map className="h-3.5 w-3.5 mr-1" />
            Browse
          </Link>
        </Button>
      </div>
    </Card>
  );
}

export default FlowTemplateQuickCard;
